import type { CSMColourTheme } from '../../../types';
import type { HexColorString } from '../hex';

import {
	deltaE,
	hexToHSL,
	hexToRgb,
	hexToRgbA,
	HSLToRGB,
	relativeLuminanceW3C,
	rgbToHex,
} from './color-utils';
import { argbFromRgba, rgbaFromArgb } from './hct-color-utils/color-utils';
import { Contrast } from './hct-color-utils/contrast';
import { Hct } from './hct-color-utils/hct';

const lowLuminanceContrastRatios = [1.12, 1.33, 2.03, 2.73, 3.33, 4.27, 5.2, 6.62, 12.46, 14.25];
const highLuminanceContrastRatios = [1.08, 1.24, 1.55, 1.99, 2.45, 3.34, 4.64, 6.1, 10.19, 12.6];

export const getClosestColorIndex = (themeRamp: HexColorString[], brandColor: HexColorString) => {
	// Iterate over themeRamp and find whichever color is closest to brandColor
	let closestColorIndex = 0;
	let closestColorDistance: number | null = null;

	themeRamp.forEach((value: HexColorString, index: number) => {
		const distance = deltaE(hexToRgb(value), hexToRgb(brandColor));
		if (closestColorDistance === null || distance < (closestColorDistance as number)) {
			closestColorIndex = index;
			closestColorDistance = distance;
		}
	});
	return closestColorIndex;
};

export const generateColors = (brandColor: HexColorString): HexColorString[] => {
	// Determine luminance
	const HSLBrandColorHue = hexToHSL(brandColor)[0];
	const baseRgb = HSLToRGB(HSLBrandColorHue, 100, 60);
	const isLowLuminance = relativeLuminanceW3C(baseRgb[0], baseRgb[1], baseRgb[2]) < 0.4;
	// Choose right palette
	const themeRatios = isLowLuminance ? lowLuminanceContrastRatios : highLuminanceContrastRatios;
	const brandRgba = hexToRgbA(brandColor);
	const hctColor = Hct.fromInt(
		argbFromRgba({
			r: brandRgba[0],
			g: brandRgba[1],
			b: brandRgba[2],
			a: brandRgba[3],
		}),
	);
	const themeRamp = themeRatios.map((contrast) => {
		const rgbaColor = rgbaFromArgb(
			Hct.from(
				hctColor.hue,
				hctColor.chroma,
				Contrast.darker(100, contrast) + 0.25, // Material's utils provide an offset
			).toInt(),
		);
		return rgbToHex(rgbaColor.r, rgbaColor.g, rgbaColor.b);
	});

	const closestColorIndex = getClosestColorIndex(themeRamp, brandColor);

	// Replace closet color with brandColor
	const updatedThemeRamp = [...themeRamp];
	updatedThemeRamp[closestColorIndex] = brandColor;

	return updatedThemeRamp;
};

export const generateColorRamps = (primaryColor: HexColorString): CSMColourTheme => {
	const colors = generateColors(primaryColor);

	return {
		color100: colors[0],
		color200: colors[1],
		color300: colors[2],
		color400: colors[3],
		color500: colors[4],
		color600: colors[5],
		color700: colors[6],
		color800: colors[7],
		color900: colors[8],
		color1000: colors[9],
	};
};
